import React, { createContext, useCallback, useContext, useEffect, useReducer, useRef } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import { getMyChatRooms } from "../services/chatService";
import {
  connectChatHub,
  disconnectChatHub,
  subscribeLiveNotification,
  subscribeChatHistory,
  subscribeChatMessage,
  subscribeChatMessagesBatch,
  subscribeChatMessageDeleted,
  subscribeChatMessageEdited,
  subscribeChatMessageDelivered,
  subscribeChatMessageSeen,
  subscribeChatRoomUsers,
  subscribeChatUserOnline,
  subscribeChatUserOffline,
  subscribePendingChatMessage,
  subscribeChatMessageRejected,
  subscribeReconnected,
  joinChatGroup,
  leaveChatGroup,
  getChatHistory as requestChatHistory,
  sendChatMessage,
  editChatMessage,
  deleteChatMessage,
  acknowledgeChatDelivery,
  markChatMessageSeen,
  getChatRoomUsers,
} from "../services/signalrService";
import { getPendingChatMessages, approveChatMessage, rejectChatMessage } from "../services/chatMediaApprovalService";
import { normalizeMessage, mergeMessageUpdate } from "../utils/chatHelpers";
import { resolveCurrentUserId } from "../utils/eventPermissions";
import { extractListResult } from "../utils/apiHelpers";

const ChatContext = createContext(null);
const ROOMS_PAGE_SIZE = 20;

const initialRoomState = { messages: [], messageMap: {}, isLoading: false };

const initialState = {
  rooms: [],
  roomsLoading: false,
  roomsLoadingMore: false,
  roomsPage: 1,
  roomsHasMore: false,
  byEventId: {},
  participants: [],
  presenceMap: {},
  activeEventId: null, // room currently open in ChatThreadPage, if any
  // Count of chat messages awaiting approval in the active room only —
  // ChatMediaApprovalService.GetPendingChatMessagesAsync's Total, refetched
  // (not incremented locally) whenever PendingChatMessageSubmitted fires, to
  // avoid any risk of client-side drift from the server's real count.
  pendingApprovalCount: 0,
};

/** A room row matches by either id field — inbox rows are keyed by `Id`, but the thread route/join/send calls all use `EventId`. */
const roomMatchesEventId = (room, eventId) =>
  eventId != null && (String(room.EventId) === String(eventId) || String(room.Id) === String(eventId));

const zeroUnreadFor = (rooms, eventId) =>
  rooms.map((r) => (roomMatchesEventId(r, eventId) ? { ...r, UnreadMessageCount: 0 } : r));

function chatReducer(state, action) {
  switch (action.type) {
    case "SET_ROOMS_LOADING":
      return { ...state, roomsLoading: action.loading };
    case "SET_ROOMS_LOADING_MORE":
      return { ...state, roomsLoadingMore: action.loading };
    case "SET_ROOMS": {
      const rooms = action.append ? [...state.rooms, ...action.rooms] : action.rooms;
      return {
        ...state,
        // The open room's unread count is authoritative locally the moment
        // it's opened (see MARK_ROOM_READ) — a rooms refetch that raced
        // ahead of the server's own read-receipt bookkeeping must not
        // un-clear that badge while the room is actively being viewed.
        rooms: state.activeEventId != null ? zeroUnreadFor(rooms, state.activeEventId) : rooms,
        roomsHasMore: action.rooms.length >= ROOMS_PAGE_SIZE,
        roomsPage: action.page,
      };
    }
    case "MARK_ROOM_READ":
      return { ...state, rooms: zeroUnreadFor(state.rooms, action.eventId) };
    case "SET_ACTIVE_EVENT":
      return { ...state, activeEventId: action.eventId };
    case "SET_ROOM_LOADING": {
      const room = state.byEventId[action.eventId] || initialRoomState;
      return { ...state, byEventId: { ...state.byEventId, [action.eventId]: { ...room, isLoading: action.loading } } };
    }
    case "SET_HISTORY": {
      // GetChatHistory never includes another sender's still-Pending item (only
      // its own sender gets that back) — so a moderator-preview bubble loaded via
      // loadPendingMessages would otherwise get silently wiped out by the next
      // history refresh (room reopen, reconnect) racing ahead of it. Carry any
      // such preview forward across the refresh unless the new history already
      // has that id (it was approved/rejected in the meantime).
      const previousMessages = state.byEventId[action.eventId]?.messages || [];
      const incomingIds = new Set(action.messages.map((m) => m.id));
      const previewsToKeep = previousMessages.filter(
        (m) => m.approvalStatus === "Pending" && m.moderationId != null && !incomingIds.has(m.id)
      );
      const merged = [...action.messages, ...previewsToKeep].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      const messageMap = {};
      merged.forEach((m) => { messageMap[m.id] = m; });
      return {
        ...state,
        byEventId: { ...state.byEventId, [action.eventId]: { messages: merged, messageMap, isLoading: false } },
      };
    }
    case "ADD_MESSAGE": {
      const room = state.byEventId[action.eventId] || initialRoomState;
      const existing = room.messageMap[action.message.id];
      // A moderator-preview placeholder (still Pending locally) gets replaced
      // in place once the real broadcast for the same id arrives (Approved, or
      // a refreshed preview) — a normal duplicate (already resolved) is still
      // deduped as before.
      if (existing && existing.approvalStatus !== "Pending") return state;
      const messages = existing
        ? room.messages.map((m) => (m.id === action.message.id ? action.message : m))
        : [...room.messages, action.message].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      return {
        ...state,
        byEventId: {
          ...state.byEventId,
          [action.eventId]: { ...room, messages, messageMap: { ...room.messageMap, [action.message.id]: action.message } },
        },
      };
    }
    case "UPDATE_MESSAGE": {
      const room = state.byEventId[action.eventId];
      // Simplification vs. mobile's pendingMessageUpdates queue: an update
      // for a message we haven't loaded yet (out-of-order delivery) is
      // dropped rather than stashed — acceptable since GetChatHistory is
      // requested immediately on room open and these events are rare in
      // that narrow window.
      if (!room || !room.messageMap[action.messageId]) return state;
      let updated;
      try {
        updated = mergeMessageUpdate(room.messageMap[action.messageId], action.raw);
      } catch (err) {
        // A live payload shape we haven't seen before must never crash the
        // whole page — drop this one update and keep the message as-is.
        // eslint-disable-next-line no-console
        console.error("[chat] failed to merge message update", action.raw, err);
        return state;
      }
      return {
        ...state,
        byEventId: {
          ...state.byEventId,
          [action.eventId]: {
            ...room,
            messageMap: { ...room.messageMap, [action.messageId]: updated },
            messages: room.messages.map((m) => (m.id === action.messageId ? updated : m)),
          },
        },
      };
    }
    case "DELETE_MESSAGE": {
      const room = state.byEventId[action.eventId];
      if (!room) return state;
      const messageMap = { ...room.messageMap };
      delete messageMap[action.messageId];
      return {
        ...state,
        byEventId: { ...state.byEventId, [action.eventId]: { ...room, messageMap, messages: room.messages.filter((m) => m.id !== action.messageId) } },
      };
    }
    case "SET_PARTICIPANTS":
      return { ...state, participants: action.participants };
    case "SET_PRESENCE":
      if (action.userId == null) return state;
      return { ...state, presenceMap: { ...state.presenceMap, [action.userId]: action.online } };
    case "SET_PENDING_APPROVAL_COUNT":
      return { ...state, pendingApprovalCount: action.count };
    case "RESET_ACTIVE":
      return { ...state, participants: [], presenceMap: {}, pendingApprovalCount: 0 };
    case "RESET_ALL":
      return initialState;
    default:
      return state;
  }
}

/**
 * Global chat state — inbox rooms list + the currently-open room's message
 * cache, fed by the shared SignalR hub connection (see signalrService.js).
 * Follows the same provider shape as NotificationContext.jsx: init/reset on
 * `token` change, a live-subscribe effect, everything exposed via one hook.
 */
export const ChatProvider = ({ children }) => {
  const { t } = useTranslation("chat");
  const { token, user } = useAuth();
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const activeEventIdRef = useRef(null);
  const joinedEventIdRef = useRef(null); // set only once JoinGroup's invoke actually resolves
  const currentUserId = resolveCurrentUserId(user);

  const loadRooms = useCallback((page = 1) => {
    dispatch({ type: page === 1 ? "SET_ROOMS_LOADING" : "SET_ROOMS_LOADING_MORE", loading: true });
    // roomType: null -> every conversation (regular event chats + @Rooms),
    // matching mobile's inbox screen. RoomList.jsx instead passes
    // "Personal" deliberately, to narrow to @Rooms only for that screen.
    return getMyChatRooms(null, page, ROOMS_PAGE_SIZE)
      .then((res) => {
        const rooms = extractListResult(res.data);
        dispatch({ type: "SET_ROOMS", rooms, append: page > 1, page });
      })
      .catch(() => toast.error(t("inbox.loadError")))
      .finally(() => {
        dispatch({ type: "SET_ROOMS_LOADING", loading: false });
        dispatch({ type: "SET_ROOMS_LOADING_MORE", loading: false });
      });
  }, [t]);

  const loadMoreRooms = useCallback(() => loadRooms(state.roomsPage + 1), [loadRooms, state.roomsPage]);

  const unreadTotal = state.rooms.reduce((sum, r) => sum + (r.UnreadMessageCount || 0), 0);

  // Initial load + reset on logout.
  useEffect(() => {
    if (!token) {
      dispatch({ type: "RESET_ALL" });
      return;
    }
    loadRooms(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Shared hub connection + inbox refresh whenever a chat-related live
  // notification arrives (mirrors mobile inbox's CHAT_MESSAGE/
  // CHAT_MESSAGE_A_ROOM push-triggered refetch).
  useEffect(() => {
    if (!token) {
      disconnectChatHub();
      return undefined;
    }

    connectChatHub(() => localStorage.getItem("token") || "");
    const unsubscribe = subscribeLiveNotification((payload) => {
      const identifier = payload?.data?.Identifier || payload?.data?.identifier;
      if (identifier === "CHAT_MESSAGE" || identifier === "CHAT_MESSAGE_A_ROOM") loadRooms(1);
    });

    return () => {
      unsubscribe();
      disconnectChatHub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Message-level live wiring for whichever room is currently open.
  useEffect(() => {
    if (!token) return undefined;

    // A shape we haven't seen before must never crash the whole page — log
    // and skip that one message rather than letting normalizeMessage throw
    // out of a SignalR callback (already burned once on MessageDelivered).
    const safeNormalize = (raw) => {
      try {
        return normalizeMessage(raw);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[chat] failed to normalize message", raw, err);
        return null;
      }
    };

    const unsubHistory = subscribeChatHistory((rawMessages) => {
      const eventId = activeEventIdRef.current;
      if (!eventId) return;
      const messages = (rawMessages || [])
        .map(safeNormalize)
        .filter(Boolean)
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      dispatch({ type: "SET_HISTORY", eventId, messages });
    });

    const unsubMessage = subscribeChatMessage((raw) => {
      const message = safeNormalize(raw);
      if (!message) return;

      // Only append to a room's local message cache if it's the one
      // actually open right now — a message for a *different* room must
      // never land in whichever thread happens to be on screen. The
      // inbox-list refresh below, on the other hand, is unconditional: it
      // has to fire whether or not any thread is open (previously this
      // whole handler bailed out early when nothing was open, so the
      // inbox page itself never got live updates).
      const activeEventId = activeEventIdRef.current;
      const messageEventId = raw?.eventId ?? raw?.EventId;
      if (activeEventId != null && (messageEventId == null || String(messageEventId) === String(activeEventId))) {
        dispatch({ type: "ADD_MESSAGE", eventId: activeEventId, message });
        if (message.senderId != null && currentUserId != null && String(message.senderId) !== String(currentUserId)) {
          acknowledgeChatDelivery(message.id);
          setTimeout(() => markChatMessageSeen(message.id), 500);
        }
      }

      loadRooms(1); // keep inbox previews/unread counts fresh, thread open or not
    });

    const unsubBatch = subscribeChatMessagesBatch((rawMessages) => {
      const eventId = activeEventIdRef.current;
      if (!eventId) return;
      (rawMessages || []).forEach((raw) => {
        const message = safeNormalize(raw);
        if (message) dispatch({ type: "ADD_MESSAGE", eventId, message });
      });
    });

    const unsubDeleted = subscribeChatMessageDeleted((_msgId, messageId) => {
      const eventId = activeEventIdRef.current;
      if (eventId) dispatch({ type: "DELETE_MESSAGE", eventId, messageId });
    });

    const handleUpdate = (raw) => {
      const eventId = activeEventIdRef.current;
      const messageId = raw?.messageId ?? raw?.MessageId ?? raw?.id ?? raw?.Id;
      if (eventId && messageId != null) dispatch({ type: "UPDATE_MESSAGE", eventId, messageId, raw });
    };
    const unsubEdited = subscribeChatMessageEdited(handleUpdate);
    const unsubDelivered = subscribeChatMessageDelivered(handleUpdate);
    const unsubSeen = subscribeChatMessageSeen(handleUpdate);

    const unsubRoomUsers = subscribeChatRoomUsers((result) => dispatch({ type: "SET_PARTICIPANTS", participants: result || [] }));
    const unsubOnline = subscribeChatUserOnline((u) => dispatch({ type: "SET_PRESENCE", userId: u?.userId ?? u?.UserId, online: true }));
    const unsubOffline = subscribeChatUserOffline((u) => dispatch({ type: "SET_PRESENCE", userId: u?.userId ?? u?.UserId, online: false }));

    // Only a moderator ever gets this event at all (ChatMediaApprovalService
    // rejects the underlying GET for anyone else), so refetching here on the
    // bare "something's waiting" ping needs no permission check of its own —
    // a non-moderator viewing the room simply never receives it.
    const unsubPending = subscribePendingChatMessage((payload) => {
      const roomId = payload?.ChatRoomId ?? payload?.chatRoomId;
      const eventId = activeEventIdRef.current;
      if (eventId == null || roomId == null || String(roomId) !== String(eventId)) return;
      getPendingChatMessages(eventId, 1, 1)
        .then((res) => dispatch({ type: "SET_PENDING_APPROVAL_COUNT", count: res.data?.Result?.Total ?? 0 }))
        .catch(() => {});
      loadPendingMessages(eventId);
    });

    // A rejection is personal to the sender (ChatHub/ChatMediaApprovalService
    // target Clients.Caller/Clients.User only) — if we're that sender and the
    // message is still in our own local cache (rendered Pending), patch it in
    // place; MessageBubble reads message.approvalStatus to show the outcome.
    const unsubRejected = subscribeChatMessageRejected((payload) => {
      const eventId = activeEventIdRef.current;
      const roomId = payload?.chatRoomId ?? payload?.ChatRoomId;
      const messageId = payload?.messageId ?? payload?.MessageId;
      if (eventId == null || messageId == null || roomId == null || String(roomId) !== String(eventId)) return;
      dispatch({
        type: "UPDATE_MESSAGE",
        eventId,
        messageId,
        raw: { approvalStatus: "Rejected", rejectionReason: payload?.rejectionReason ?? payload?.RejectionReason },
      });
    });

    // SignalR group membership is tied to the physical connection, not this
    // HubConnection object — withAutomaticReconnect() gets a brand-new
    // connection id after any drop (idle proxy/load-balancer timeout is the
    // common real-world trigger, easily 10+ minutes into an open thread),
    // silently un-joining whatever room was open. Nothing else self-heals
    // this: invoke() calls (send/edit/delete) still succeed the whole time
    // since ensureConnected only checks Connected state, so there's no
    // error to notice — the room just stops receiving ReceiveMessage
    // broadcasts until this re-runs the same join+catch-up openRoom() does.
    const unsubReconnected = subscribeReconnected(() => {
      const eventId = activeEventIdRef.current;
      if (!eventId) return;
      joinChatGroup(eventId).then(() => {
        if (activeEventIdRef.current !== eventId) return; // room changed mid-rejoin
        requestChatHistory(eventId);
        getChatRoomUsers(eventId);
        refreshPendingApprovalCount(eventId);
        loadPendingMessages(eventId);
      });
      loadRooms(1);
    });

    return () => {
      unsubHistory(); unsubMessage(); unsubBatch(); unsubDeleted();
      unsubEdited(); unsubDelivered(); unsubSeen();
      unsubRoomUsers(); unsubOnline(); unsubOffline();
      unsubPending(); unsubRejected(); unsubReconnected();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentUserId]);

  // Only ever leave a room we've *confirmed* joined (joinedEventIdRef set
  // after JoinGroup's invoke actually resolves) — otherwise a leave fired
  // before the join round-trip completes (e.g. React StrictMode's
  // dev-only double-invoke of a mount effect) gets rejected server-side
  // for a group we were never really added to yet. Harmless either way
  // (invoke() already catches/logs), but this avoids the noise and the
  // wasted round-trip.
  const leaveIfJoined = useCallback((eventId) => {
    if (joinedEventIdRef.current === eventId) {
      leaveChatGroup(eventId, currentUserId);
      joinedEventIdRef.current = null;
    }
  }, [currentUserId]);

  // Silently ignored for a non-moderator (403/ForbidChallenge) — this is
  // fetched unconditionally on every room open rather than gated by a
  // permission the context doesn't itself know, same trust-the-backend
  // approach the SignalR pending-ping handler above takes.
  const refreshPendingApprovalCount = useCallback((eventId) => {
    if (eventId == null) return Promise.resolve();
    return getPendingChatMessages(eventId, 1, 1)
      .then((res) => dispatch({ type: "SET_PENDING_APPROVAL_COUNT", count: res.data?.Result?.Total ?? 0 }))
      .catch(() => {});
  }, []);

  // Feeds MessageBubble's inline blurred "needs approval" preview — pulls the
  // room's actual pending items (not just the count) into the same message
  // list the thread already renders, so a moderator sees them in place rather
  // than having to open the separate queue modal. Same silent 403 handling
  // as refreshPendingApprovalCount for a non-moderator. Capped at 50 - the
  // Pending Approval modal's own pagination is still there for a room with
  // more waiting than that.
  const loadPendingMessages = useCallback((eventId) => {
    if (eventId == null) return Promise.resolve();
    return getPendingChatMessages(eventId, 1, 50)
      .then((res) => {
        const items = res.data?.Result?.Items || [];
        items.forEach((item) => {
          dispatch({
            type: "ADD_MESSAGE",
            eventId,
            message: {
              id: item.MessageId,
              text: item.Message,
              messageType: item.MessageType,
              createdAt: item.Timestamp,
              senderId: item.UserId,
              senderName: item.SenderName,
              senderAvatar: item.SenderDisplayPicture,
              senderUserType: undefined,
              isEdited: false,
              deliveredTo: [],
              seenBy: [],
              deliveredToAll: false,
              seenByAll: false,
              approvalStatus: "Pending",
              rejectionReason: null,
              // The integer ChatMessages.Id the Approve/Reject REST endpoints
              // need — normalizeMessage's `id` is always the MessageId GUID,
              // which those endpoints don't accept. Doubles as the marker that
              // distinguishes "a moderator's preview of someone else's pending
              // item" from "my own pending send" (SET_HISTORY relies on this).
              moderationId: item.Id,
            },
          });
        });
      })
      .catch(() => {});
  }, []);

  // Shared by MessageBubble's inline Approve/Reject buttons and
  // PendingApprovalModal, so acting from either place stays in sync: an
  // approve optimistically flips the local preview to Approved (the room-wide
  // broadcast that follows is a no-op against the same id per ADD_MESSAGE's
  // dedupe-or-replace rule above), a reject removes it immediately - it never
  // gets a broadcast of its own to react to otherwise, and
  // ChatMessageRejected only ever reaches the *sender's* connection, not the
  // moderator's.
  const approveMessage = useCallback((moderationId, messageGuid, applyToFutureUploads = false) =>
    approveChatMessage(moderationId, applyToFutureUploads).then((res) => {
      const eventId = activeEventIdRef.current;
      if (eventId != null && messageGuid) {
        dispatch({ type: "UPDATE_MESSAGE", eventId, messageId: messageGuid, raw: { approvalStatus: "Approved" } });
      }
      refreshPendingApprovalCount(eventId);
      return res;
    }), [refreshPendingApprovalCount]);

  const rejectMessage = useCallback((moderationId, messageGuid, reason = null, applyToFutureUploads = false) =>
    rejectChatMessage(moderationId, reason, applyToFutureUploads).then((res) => {
      const eventId = activeEventIdRef.current;
      if (eventId != null && messageGuid) {
        dispatch({ type: "DELETE_MESSAGE", eventId, messageId: messageGuid });
      }
      refreshPendingApprovalCount(eventId);
      return res;
    }), [refreshPendingApprovalCount]);

  const openRoom = useCallback((eventId) => {
    const idStr = String(eventId);
    if (activeEventIdRef.current && activeEventIdRef.current !== idStr) {
      leaveIfJoined(activeEventIdRef.current);
    }
    activeEventIdRef.current = idStr;
    dispatch({ type: "RESET_ACTIVE" });
    dispatch({ type: "SET_ACTIVE_EVENT", eventId: idStr });
    // Clear the sidebar/inbox unread badge for this room the moment it's
    // opened, rather than waiting on a rooms refetch to catch up with
    // whatever "mark as read" bookkeeping the server does on its own time.
    dispatch({ type: "MARK_ROOM_READ", eventId: idStr });
    dispatch({ type: "SET_ROOM_LOADING", eventId: idStr, loading: true });
    joinChatGroup(idStr).then(() => {
      // Only mark as joined if this is still the room we want to be in —
      // a fast openRoom(A) -> openRoom(B) shouldn't mark A as joined once
      // its late-resolving invoke comes back.
      if (activeEventIdRef.current === idStr) joinedEventIdRef.current = idStr;
      requestChatHistory(idStr);
      getChatRoomUsers(idStr);
    });
    refreshPendingApprovalCount(idStr);
    loadPendingMessages(idStr);
  }, [leaveIfJoined, refreshPendingApprovalCount, loadPendingMessages]);

  const closeRoom = useCallback(() => {
    if (activeEventIdRef.current) {
      leaveIfJoined(activeEventIdRef.current);
      activeEventIdRef.current = null;
    }
    dispatch({ type: "RESET_ACTIVE" });
    dispatch({ type: "SET_ACTIVE_EVENT", eventId: null });
  }, [leaveIfJoined]);

  // Leave whatever room is open if the whole app unmounts (rare — provider
  // lives at the App.js root — but keeps the hub-side group membership tidy).
  useEffect(() => () => {
    if (activeEventIdRef.current) leaveIfJoined(activeEventIdRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMessage = useCallback((eventId, text) => sendChatMessage(eventId, currentUserId, text, "text"), [currentUserId]);
  const editMessage = useCallback((message, newText) => editChatMessage(message.id, message.id, newText), []);
  const removeMessage = useCallback((message) => deleteChatMessage(message.id, message.id), []);

  const value = {
    currentUserId,
    rooms: state.rooms,
    roomsLoading: state.roomsLoading,
    roomsLoadingMore: state.roomsLoadingMore,
    roomsHasMore: state.roomsHasMore,
    unreadTotal,
    loadRooms,
    loadMoreRooms,
    getRoomMessages: (eventId) => state.byEventId[eventId]?.messages || [],
    isRoomLoading: (eventId) => !!state.byEventId[eventId]?.isLoading,
    participants: state.participants,
    presenceMap: state.presenceMap,
    openRoom,
    closeRoom,
    sendMessage,
    editMessage,
    removeMessage,
    pendingApprovalCount: state.pendingApprovalCount,
    refreshPendingApprovalCount,
    approveMessage,
    rejectMessage,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => useContext(ChatContext);
